import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { financeApi, formatCents, type Invoice } from "@/services/finance";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { Can } from "@/auth/permissions";

type ListTab = "issue" | "receipt";
type StatusTab = "all" | "draft" | "synced" | "paid" | "void";

const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: "all", label: "全部状态" },
  { key: "draft", label: "待开" },
  { key: "synced", label: "已同步" },
  { key: "paid", label: "已收款" },
  { key: "void", label: "作废" },
];

/** QB 同步状态列（canvas page-74：已同步/草稿/未同步）。 */
function qbSyncState(inv: Invoice): { text: string; color: string } {
  if (inv.status === "draft") return { text: "草稿", color: "text-slate-500" };
  if (inv.qb_doc_id) return { text: "已同步", color: "text-emerald-500" };
  return { text: "未同步", color: "text-amber-500" };
}
/** 收款状态列（canvas page-74：已收款/待付款/部分/逾期/—）。 */
function payState(inv: Invoice): { text: string; color: string } {
  if (inv.status === "void") return { text: "作废", color: "text-slate-400" };
  if (inv.status === "draft") return { text: "—", color: "text-slate-400" };
  if (inv.status === "paid") return { text: "已收款", color: "text-emerald-500" };
  if (inv.status === "overdue") return { text: "逾期", color: "text-red-500" };
  if (inv.status === "partial") return { text: "部分付款", color: "text-amber-500" };
  return { text: "待付款", color: "text-amber-500" };
}

// 收据 OCR 归档 · demo（无端点·真实接 uploads/OCR 待后续）
const RECEIPTS_DEMO = [
  { name: "黄石小屋_April.pdf", status: "已推 QB" },
  { name: "ParkPass_4月.jpg", status: "已推 QB" },
  { name: "油费收据_5-08.pdf", status: "OCR 中" },
  { name: "司导考勤_4月.xlsx", status: "已推 QB" },
];

/** 财务 · 发票管理 Invoices（/admin/finance/invoices · canvas page-74 · QB 业务壳）。 */
export function InvoicesPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ListTab>("issue");
  const [status, setStatus] = useState<StatusTab>("all");
  const [search, setSearch] = useState("");

  const ovQ = useQuery({ queryKey: ["finance", "overview"], queryFn: () => financeApi.overview() });
  const invQ = useQuery({ queryKey: ["finance", "invoices", "all"], queryFn: () => financeApi.listInvoices() });

  const all = invQ.data ?? [];
  const qbConnected = ovQ.data?.qb_connection_status === "connected";
  const draftCount = all.filter((i) => i.status === "draft").length;
  const syncedCount = all.filter((i) => i.qb_doc_id).length;

  const rows = all
    .filter((i) => status === "all" || (status === "synced" ? !!i.qb_doc_id : status === "draft" ? i.status === "draft" : status === "paid" ? i.status === "paid" : status === "void" ? i.status === "void" : true))
    .filter((i) => !search || i.customer.includes(search) || i.invoice_no.includes(search) || (i.order_no ?? "").includes(search));

  const kpis = [
    { label: "本月开 Invoice", value: String(ovQ.data?.invoices_total ?? all.length), sub: "总额开票", color: "text-blue-500" },
    { label: "QB 同步成功率", value: qbConnected ? "100%" : "—", sub: qbConnected ? "0 失败" : "QB 未对接", color: "text-emerald-500" },
    { label: "待开 Invoice", value: String(draftCount), sub: "🔴 财务在我们这录入", color: "text-amber-500" },
    { label: "收据归档（OCR）", value: "—", sub: "待接 OCR（uploads）", color: "text-violet-600" },
  ];

  return (
    <PageShell
      title="📄 发票管理 · QuickBooks 业务壳 + 一键跳转"
      subtitle="Q5 改：对接 QB API · 数据双向同步 · 深度账务工作跳 QB 原生界面"
      actions={
        <div className="flex gap-2">
          <span className={`flex h-9 items-center rounded-lg px-3 text-[12px] font-semibold ${qbConnected ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>🔗 QB {qbConnected ? "已对接" : "未对接"}</span>
          <Can perm="finance.invoices.create">
            <Link to="/admin/finance/invoices/new" className="flex h-9 items-center rounded-lg bg-blue-600 px-3 text-[12px] font-semibold text-white hover:bg-blue-700">+ 新建 Invoice</Link>
          </Can>
        </div>
      }
    >
      <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">🔒 仅老板 + 财务可访问 · 经理可在 B2B 客户详情看自己客户的 invoice 状态（只读）</div>

      {/* QB 集成状态卡 · 真实 v1 状态 */}
      <Card title="🔗 QuickBooks Online 集成状态" action={<span className="text-[12px] text-slate-500">深度对接 · 业务壳运营视角 + QB 跳转深度账务</span>}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className={`rounded-lg border p-4 ${qbConnected ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
            <div className={`text-[12px] font-bold ${qbConnected ? "text-emerald-800" : "text-amber-800"}`}>{qbConnected ? "🟢 连接状态：已对接" : "🟡 连接状态：未对接"}</div>
            <div className="mt-1 space-y-0.5 text-[11px] text-slate-600">
              <div>QB Company：{qbConnected ? "悠途黄石 LLC" : "—"}</div>
              <div>上次同步：{ovQ.data?.qb_last_sync_at ? new Date(ovQ.data.qb_last_sync_at).toLocaleString("zh-CN") : "—"}</div>
              <div className="text-amber-700">QB 对接压淡季 Phase 6（路线图）· 现阶段业务壳运营</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-[12px] font-bold text-blue-800">📊 本月同步数据</div>
            <div className="mt-1 space-y-0.5 text-[11px] text-slate-600">
              <div>· Invoice {ovQ.data?.invoices_total ?? 0} 张（↗ QB）</div>
              <div>· Customer ↔ 双向 · Payment ↙ QB</div>
              <div className="text-slate-400">QB 未对接 · 数据待接入</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-[12px] font-bold text-violet-800">⚙ 同步规则</div>
            <div className="mt-1 space-y-0.5 text-[11px] text-slate-600">
              <div>· 系统建单 → 实时推 QB</div>
              <div>· QB 标记收款 → webhook 回推</div>
              <div>· 客户主数据 ↔ 双向</div>
            </div>
            <div className="mt-2 flex gap-3 text-[12px] font-medium text-slate-700">
              <Link to="/admin/api-integration/sync-logs" className="hover:text-blue-600">查看同步日志</Link>
              <Link to="/admin/api-integration/qb" className="hover:text-blue-600">管理对接配置 →</Link>
            </div>
          </div>
        </div>
        {/* 右侧 4 KPI */}
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-xl border border-slate-200 p-4">
              <div className="text-[11px] font-semibold text-slate-500">{k.label}</div>
              <div className={`mt-1.5 text-[22px] font-bold leading-none ${k.color}`}>{k.value}</div>
              <div className="mt-1.5 text-[10px] text-slate-400">{k.sub}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Q5 架构说明卡 · 静态设计说明 */}
      <Card title="💡 Q5 改决策 · 业务壳 + QB 跳转架构">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ArchCol title="🏠 我们这边（运营视角）" titleColor="text-blue-800" items={["业务字段录入（订单/客户/金额）", "实时业务全景 + 老板告警", "USD 统一结算（CNY 收款手工对账）", "跨平台数据（订单+渠道+利润）", "一键 → QB 深度"]} />
          <ArchCol title="💼 QuickBooks（账务视角）" titleColor="text-emerald-800" items={["PDF 发票自动生成", "Sales Tax 自动计算（美国）", "复式记账 / 凭证 / 报税", "银行对账 / 现金流复盘", "财务深度工作主场"]} />
          <ArchCol title="🔄 数据同步规则" titleColor="text-amber-800" items={["我们录单 → 实时推 QB", "QB 收款 → webhook 回推标记", "客户主数据双向同步", "报表数据按需拉 QB", "失败重试 3 次 → API 集成中心"]} />
        </div>
        <div className="mt-3 rounded-lg bg-emerald-50 px-4 py-2 text-[12px] font-semibold text-emerald-800">✅ 在我们这录单比直接在 QB 还快（订单字段自动带入）· 深度复盘/报税一键跳 QB · 0 学习成本</div>
      </Card>

      {/* Invoice 列表卡 */}
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-4 border-b border-slate-100 pb-3">
          <button onClick={() => setTab("issue")} className={`text-[13px] ${tab === "issue" ? "font-bold text-blue-600" : "font-medium text-slate-500"}`}>📤 开出 Invoice（→ QB）</button>
          <button onClick={() => setTab("receipt")} className={`text-[13px] ${tab === "receipt" ? "font-bold text-blue-600" : "font-medium text-slate-500"}`}>📥 收到收据（OCR 归档）</button>
        </div>

        {tab === "issue" ? (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {STATUS_TABS.map((t) => (
                <button key={t.key} onClick={() => setStatus(t.key)} className={`h-8 rounded-lg px-3 text-[12px] ${status === t.key ? "bg-blue-600 font-semibold text-white" : "font-medium text-slate-700 hover:bg-slate-100"}`}>{t.label}{t.key === "draft" ? ` ${draftCount}` : t.key === "synced" ? ` ${syncedCount}` : ""}</button>
              ))}
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 搜索客户/发票号/订单号" className="ml-auto h-8 w-48 rounded-lg border border-slate-200 px-3 text-[12px] focus:border-blue-500 focus:outline-none" />
            </div>
            {invQ.isLoading ? <LoadingState /> : invQ.isError ? (
              <div className="py-6 text-center">
                <p className="text-[13px] font-semibold text-red-700">发票数据加载失败</p>
                <p className="mt-0.5 text-[11px] text-red-500">{(invQ.error as Error)?.message || "网络错误"}</p>
                <button onClick={() => invQ.refetch()} disabled={invQ.isFetching} className="mt-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{invQ.isFetching ? "重试中…" : "点击重试"}</button>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <tr><th className="px-4 py-2 text-left">QB Invoice ID</th><th className="px-4 py-2 text-left">客户 / 订单关联</th><th className="px-4 py-2 text-left">开票日</th><th className="px-4 py-2 text-left">金额</th><th className="px-4 py-2 text-left">QB 同步</th><th className="px-4 py-2 text-left">收款状态</th><th className="px-4 py-2 text-right">操作</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((inv) => {
                      const qb = qbSyncState(inv);
                      const pay = payState(inv);
                      return (
                        <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-[12px] text-slate-600">{inv.qb_doc_id ?? (inv.status === "draft" ? "— 待开 —" : inv.invoice_no)}</td>
                          <td className="px-4 py-3"><div className="text-[12px] font-semibold text-slate-900">{inv.customer}</div><div className="text-[10px] text-slate-400">订单关联 {inv.order_no ?? "—"}</div></td>
                          <td className="px-4 py-3 text-slate-600">{inv.status === "draft" ? "—" : new Date(inv.issued_at).toLocaleDateString("zh-CN")}</td>
                          <td className="px-4 py-3">
                            <div className="font-bold text-slate-900">{formatCents(inv.amount_cents)}</div>
                            <div className="text-[10px] text-slate-400">含税{inv.paid_cents > 0 && inv.paid_cents < inv.amount_cents ? ` · 未收 ${formatCents(inv.amount_cents - inv.paid_cents)}` : ""}</div>
                          </td>
                          <td className={`px-4 py-3 text-[10px] font-bold ${qb.color}`}>{qb.text}</td>
                          <td className={`px-4 py-3 text-[10px] font-bold ${pay.color}`}>{pay.text}</td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1.5">
                              <button onClick={() => alert(`${inv.customer}\n${inv.invoice_no} · ${formatCents(inv.amount_cents)}\n订单关联 ${inv.order_no ?? "—"}`)} className="rounded-md border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">详情</button>
                              <button disabled={!qbConnected || !inv.qb_doc_id} title={qbConnected ? "" : "QB 淡季接入"} className="rounded-md bg-blue-600 px-2 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">→ QB 打开</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">该状态下暂无发票</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-2 text-[11px] text-slate-400">💡 待开行点「详情」可去新建；订单关联仅有绑订单的发票显示 #号。</div>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* 最近上传 */}
            <div>
              <div className="mb-2 text-[12px] font-bold text-slate-900">📁 最近上传（{RECEIPTS_DEMO.length} 件）</div>
              <div className="flex flex-col gap-2">
                {RECEIPTS_DEMO.map((r) => (
                  <div key={r.name} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5">
                    <span className="text-[12px] text-slate-700">📷 {r.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.status === "已推 QB" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                  </div>
                ))}
                <button className="rounded-lg border border-dashed border-slate-300 px-4 py-2.5 text-[12px] font-semibold text-blue-600 hover:bg-blue-50">+ 上传新文件</button>
              </div>
            </div>
            {/* OCR 识别能力 */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-[12px] font-bold text-emerald-800">🤖 OCR 识别能力</div>
              <ul className="mt-1.5 space-y-1 text-[11px] text-emerald-700">
                <li>· 自动提取金额 / 日期 / 供应商</li>
                <li>· 自动关联应付账款条目</li>
                <li>· 自动上传到 QB Expense</li>
                <li>· 多语言：中 / 英 / PDF / 图片</li>
                <li>· 识别失败 → 财务手工补录</li>
              </ul>
              <div className="mt-2 text-[11px] font-semibold text-emerald-800">本月 OCR 识别率：92% · 8% 需手工</div>
              <div className="mt-2 text-[10px] text-slate-400">demo 数据（真实接 uploads/OCR 待后续）</div>
            </div>
          </div>
        )}
      </Card>
    </PageShell>
  );
}

function ArchCol({ title, titleColor, items }: { title: string; titleColor: string; items: string[] }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className={`text-[12px] font-bold ${titleColor}`}>{title}</div>
      <ul className="mt-1.5 space-y-1 text-[11px] text-slate-600">{items.map((it, i) => <li key={i}>· {it}</li>)}</ul>
    </div>
  );
}
